import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DomainBadge from '../../../shared/components/DomainBadge';
import { matrixCellToFlashcard, downloadAsJSON } from '../../../shared/utils/exportHelpers';

/**
 * Interactive comparison table with sticky headers, exam_hot highlighting,
 * and expandable cells.
 */
export default function MatrixView({ matrix, onExportFlashcard }) {
  const navigate = useNavigate();
  const [expandedCell, setExpandedCell] = useState(null);
  const [showOnlyHot, setShowOnlyHot] = useState(false);

  if (!matrix) return null;

  const attributes = showOnlyHot
    ? matrix.attributes.filter(a => a.exam_hot)
    : matrix.attributes;

  const handleCellClick = (attrIdx, protocol) => {
    const key = `${attrIdx}-${protocol}`;
    setExpandedCell(expandedCell === key ? null : key);
  };

  const handleExportCell = (attr, protocol) => {
    const value = attr.values[protocol];
    if (!value) return;
    const card = matrixCellToFlashcard(matrix.title, protocol, attr.name, value, matrix.domain);
    if (onExportFlashcard) {
      onExportFlashcard(card);
    } else {
      downloadAsJSON([card], `flashcard-${protocol}-${attr.name}.json`);
    }
  };

  return (
    <div className="px-4 py-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <button
            onClick={() => navigate('/matrix')}
            className="text-sm text-gray-500 hover:text-gray-700 mb-1"
          >
            ← All Comparisons
          </button>
          <h2 className="text-xl font-bold text-gray-900">{matrix.title}</h2>
          <DomainBadge domainId={matrix.domain} size="xs" className="mt-1" />
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showOnlyHot}
              onChange={(e) => setShowOnlyHot(e.target.checked)}
              className="rounded"
            />
            Exam-critical only
          </label>
          <div className="flex gap-2">
            <button
              onClick={() => navigate(`/matrix/${matrix.id}/drill/rapid`)}
              className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
            >
              Rapid Fire
            </button>
            <button
              onClick={() => navigate(`/matrix/${matrix.id}/drill/fill`)}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              Fill Blank
            </button>
            <button
              onClick={() => navigate(`/matrix/${matrix.id}/drill/recall`)}
              className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
            >
              Full Recall
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              <th className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left font-medium text-gray-600 border-b border-r border-gray-200 min-w-[180px]">
                Attribute
              </th>
              {matrix.protocols.map(protocol => (
                <th
                  key={protocol}
                  className="px-4 py-3 text-left font-medium text-gray-900 border-b border-gray-200 min-w-[150px]"
                >
                  {protocol}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {attributes.map((attr, attrIdx) => (
              <tr
                key={attrIdx}
                className={`border-b border-gray-100 ${
                  attr.exam_hot ? 'bg-yellow-50' : attrIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                }`}
              >
                <td className={`sticky left-0 z-10 px-4 py-3 font-medium border-r border-gray-200 ${
                  attr.exam_hot ? 'bg-yellow-50 text-yellow-800' : attrIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                }`}>
                  <div className="flex items-center gap-2">
                    {attr.exam_hot && <span className="text-yellow-500 text-xs">★</span>}
                    {attr.name}
                  </div>
                </td>
                {matrix.protocols.map(protocol => {
                  const value = attr.values[protocol] || '—';
                  const cellKey = `${attrIdx}-${protocol}`;
                  const isExpanded = expandedCell === cellKey;

                  // Highlight cells that differ from others
                  const allValues = matrix.protocols.map(p => attr.values[p]);
                  const allSame = allValues.every(v => v === allValues[0]);

                  return (
                    <td
                      key={protocol}
                      className={`px-4 py-3 cursor-pointer hover:bg-blue-50 transition-colors ${
                        allSame ? 'text-gray-400' : 'text-gray-900'
                      }`}
                      onClick={() => handleCellClick(attrIdx, protocol)}
                    >
                      <span className={isValueNumeric(value) ? 'font-mono' : ''}>
                        {value}
                      </span>

                      {/* Expanded cell with notes and export */}
                      {isExpanded && (
                        <div className="mt-2 p-2 bg-blue-50 rounded text-xs border border-blue-100">
                          {(() => {
                            const note = resolveCellNote(attr.notes, protocol);
                            return note ? (
                              <p className="text-blue-700 mb-2">{note}</p>
                            ) : null;
                          })()}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExportCell(attr, protocol);
                            }}
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            Export as flashcard
                          </button>
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-400 mt-3">
        {attributes.length} attributes • Click any cell for details • ★ = frequently tested
      </p>
    </div>
  );
}

function isValueNumeric(value) {
  return /^\d/.test(value) || /port|timer|sec|ms|byte/i.test(value);
}

// Resolve the note for a specific cell. Per-protocol object is the current
// shape; a bare string is a legacy shape where the same note was shared across
// every column in the row — return null for per-cell use so the row-level note
// never masquerades as protocol-specific context.
function resolveCellNote(notes, protocol) {
  if (!notes) return null;
  if (typeof notes === 'string') return null;
  return notes[protocol] || null;
}
